import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/LanguageSelector";
import { track } from "@/lib/analytics";
import { trackProductEvent } from "@/lib/productEvents";

// Static href targets — labels come from i18n at render time so the nav
// re-renders when the user toggles languages. Keeping the hrefs in a
// module-scope const keeps the data-testid mapping stable across locales.
const NAV_HREFS: { key: "howItWorks" | "experience" | "features" | "pricing" | "faq"; href: string }[] = [
  { key: "howItWorks", href: "#how-it-works" },
  { key: "experience", href: "#experience" },
  { key: "features", href: "#features" },
  { key: "pricing", href: "#pricing" },
  { key: "faq", href: "#faq" },
];

export function TopNav() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function go(href: string) {
    setOpen(false);
    if (href.startsWith("#")) {
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      setLocation(href);
    }
  }

  return (
    <header
      className={`sticky top-0 z-40 transition-all ${
        scrolled
          ? "border-b border-white/10 bg-[hsl(160,60%,8%)]/85 backdrop-blur-lg"
          : "bg-transparent"
      }`}
      data-testid="marketing-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8 lg:px-12">
        <Link
          href="/"
          className="inline-flex items-center gap-2 font-serif text-2xl font-semibold tracking-tight text-jingle-snow"
          data-testid="link-marketing-brand"
        >
          <span
            className="inline-block h-2.5 w-2.5 rounded-full bg-jingle-cranberry"
            style={{
              boxShadow: "0 0 14px hsl(0, 80%, 60%)",
              animation: "twinkle 2.5s ease-in-out infinite",
            }}
          />
          JINGLE
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV_HREFS.map((n) => (
            <button
              key={n.href}
              type="button"
              onClick={() => go(n.href)}
              className="text-sm text-jingle-snow/75 transition hover:text-jingle-snow"
              data-testid={`nav-${n.href.slice(1)}`}
            >
              {t(`marketing.nav.${n.key}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LanguageSelector />
          </div>
          <Link
            href="/login"
            className="hidden text-sm text-jingle-snow/75 transition hover:text-jingle-snow sm:inline"
            data-testid="link-marketing-signin"
          >
            {t("marketing.nav.signIn")}
          </Link>
          <Button
            onClick={() => {
              track("hero_signup_click", { source: "topnav" });
              setLocation("/create");
            }}
            className="hidden rounded-xl bg-jingle-cranberry px-4 text-sm font-semibold text-jingle-snow shadow-md hover:bg-[hsl(354,70%,42%)] sm:inline-flex"
            data-testid="button-marketing-launch-updates"
          >
            {t("marketing.nav.getLaunchUpdates")}
          </Button>
          <button
            type="button"
            aria-label={t("marketing.openMenu")}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-jingle-snow lg:hidden"
            onClick={() => setOpen((v) => !v)}
            data-testid="button-nav-menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open ? (
        <div
          className="border-t border-white/10 bg-[hsl(160,60%,8%)]/95 backdrop-blur lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4 sm:px-8">
            {NAV_HREFS.map((n) => (
              <button
                key={n.href}
                type="button"
                onClick={() => go(n.href)}
                className="rounded-lg px-3 py-2 text-left text-sm text-jingle-snow/85 hover:bg-white/5"
              >
                {t(`marketing.nav.${n.key}`)}
              </button>
            ))}
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-white/10 pt-3">
              <LanguageSelector />
              <Link
                href="/login"
                onClick={() => setOpen(false)}
                className="text-sm text-jingle-snow/85"
              >
                {t("marketing.nav.signIn")}
              </Link>
            </div>
            <Button
              onClick={() => {
                track("hero_signup_click", { source: "topnav_mobile" });
                setOpen(false);
                setLocation("/create");
              }}
              className="mt-3 h-12 w-full rounded-xl bg-jingle-cranberry text-sm font-semibold text-jingle-snow"
              data-testid="button-mobile-marketing-launch-updates"
            >
              {t("marketing.nav.getLaunchUpdates")}
            </Button>
          </div>
        </div>
      ) : null}
    </header>
  );
}
